import { type MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { useGetCompanyOwnerInfoQuery } from '../../../../../app/Slices/SevenDayApiSlice';
import { CustomChip } from '../../../../../layout/styledcomponents/CustomChip';
import UserRole from '../../../../../shared/AuthRoles';
import { capitalizeName } from '../../../../../shared/utils';
import GenericTable, { LinkType } from '../../../../shared/GenericTable';
import { capitalizedCell } from '../ReservationsReportList';
import { trackEvent } from '../../../../../app/Analytics';

export default function TopModulesListReport(params: any) {
    const processedData = useMemo(() => {
        if (!params?.data) return [];
        return params.data;
    }, [params.data]);

    const loggedInUser = UserRole();
    const { data: getCompanyDetails } = useGetCompanyOwnerInfoQuery(
        loggedInUser?.company,
    );

    const columns: MRT_ColumnDef<any>[] = [
        {
            accessorKey: 'name',
            header: 'Experience',
        },
        {
            accessorKey: 'type',
            header: 'Experience Type',
            filterVariant: 'select',
            mantineFilterSelectProps: {
                data: [
                    { label: 'Property', value: 'property' },
                    { label: 'Commercial', value: 'commercial' },
                    { label: 'Boat', value: 'boat' },
                    { label: 'Activity', value: 'activity' },
                    { label: 'Vehicle', value: 'vehicle' },
                ],
                clearable: true,
            },

            Cell: capitalizedCell,
        },

        {
            accessorKey: 'visitType',
            header: 'Visit Type',
            filterVariant: 'select',
            filterFn: (row, _columnId, filterValue) => {
                if (!filterValue) return true;
                return row.getValue('visitType') === filterValue;
            },
            mantineFilterSelectProps: {
                data: [
                    { label: 'Business', value: 'business' },
                    { label: 'Personal', value: 'personal' },
                ],
                placeholder: 'Filter by Visit Type',
                clearable: true,
            },
            Cell: ({ row, cell }) => {
                const visitType = cell.getValue<string>();
                return (
                    <CustomChip
                        label={capitalizeName(visitType)}
                        color={visitType === 'personal' ? 'yellow' : 'green'}
                        variant='outlined'
                        cursor='pointer'
                    />
                );
            },
        },

        {
            accessorKey: 'guestBookings',
            header: 'Total Guest Reservations',
            filterFn: 'betweenInclusive',
            mantineTableHeadCellProps: {
                title: 'No. of successfully completed guest reservations',
            },
        },
        {
            accessorKey: 'ownerBookings',
            header: 'Total Blocked Reservations',
            filterFn: 'betweenInclusive',
            mantineTableHeadCellProps: {
                title: 'No. of blocked reservations (maintenance, staff away, shutdown period, etc',
            },
        },
        {
            accessorKey: 'onBehalfBookings',
            header: `Total ${
                getCompanyDetails?.name ? getCompanyDetails?.name : 'On Behalf'
            } Reservations`,
            filterFn: 'betweenInclusive',
            mantineTableHeadCellProps: {
                title: 'No. of successfully completed owner reservations reserved on behalf of you',
            },
        },
        {
            accessorKey: 'completedBookings',
            header: 'Total Completed Reservations',
            filterFn: 'betweenInclusive',
            mantineTableHeadCellProps: {
                title: 'No. of successfully completed reservations during date range',
            },
        },
    ];

    const onRowClick = () => {
        trackEvent('Reports', 'Report Clicked', `Top Modules Report`);
    };

    const linkState = {
        name: 'viewExperiences',
        component: 'reports',
        url: `reports/topExperiences`,
    };

    return (
        <GenericTable
            columns={columns}
            data={processedData}
            tableName='7DAY-Top-Experiences-Report'
            loading={!params.data}
            onRowClick={onRowClick}
            linkType={LinkType.Experience}
            linkState={linkState}
        />
    );
}
